import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { useCompareContext } from 'Contexts/compareContext';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useTextContext } from 'Contexts/textContext';
import { Paragraph } from 'DesignLibrary/atoms';
import { Drawer } from 'DesignLibrary/atoms/Drawer';
import Plan from 'Models/plan';
import Text from 'Shared/Text';
import { Recommendation } from 'Types/entities';
import { AriaHidden } from 'Utils/accessibility';

import { Cell, RowContainer } from './_shared/CompareTableRow/styled';
import { ChooseButtons } from './ChooseButtons';
import { Copays } from './Copays';
import { DeductiblesLimits } from './DeductiblesLimits';
import { MainDetails } from './MainDetails';
import { PlanHeader } from './PlanHeader';
import { RxCoverage } from './PrescriptionCoverage';
import { CompareContainer, FloatingHeaderRow, FloatingHeaderRowTableView } from './styled';
import { NetworkTier } from './types';

export type { NetworkTier } from './types';

export interface CompareModalProps {
  isOpen: boolean;
  selectedPlan: Plan | Record<string, never>;
  hsaContributionLimit: number;
  maximumAvailableAnnualSihraContribution: number;
  killMedRec: boolean;
  showCompareRows: boolean;
  reviewModePlans: Plan[];
  handleClose: () => void;
  choosePlan: (plan: Recommendation) => void;
  deselectPlan: () => void;
  hsaBalance?: number;
  showOonInPbw: boolean;
}

const CompareModal = ({
  isOpen,
  selectedPlan,
  hsaContributionLimit,
  maximumAvailableAnnualSihraContribution,
  killMedRec,
  showCompareRows,
  reviewModePlans,
  handleClose,
  choosePlan,
  deselectPlan,
  hsaBalance,
  showOonInPbw,
}: CompareModalProps) => {
  const { retrieveContentfulData } = useTextContext();
  const { comparablePlans } = useCompareContext();
  const { is_network_tier_toggle_enabled } = useFeatureFlagContext();
  const [intersectionRef, inView] = useInView({ rootMargin: '-24px' });
  const [hiddenAlert, setHiddenAlert] = useState<string | null>(null);
  const [selectedTiers, setSelectedTiers] = useState<Record<string, NetworkTier>>({});

  const getDefaultTier = (plan: Plan): NetworkTier =>
    is_network_tier_toggle_enabled && plan.hasMostPreferredNetwork ? 'most_preferred' : 'in_network';

  const getSelectedTier = (plan: Plan): NetworkTier => selectedTiers[plan.planId] ?? getDefaultTier(plan);

  const setTierForPlan = (planId: string, tier: NetworkTier) =>
    setSelectedTiers((prev) => ({ ...prev, [planId]: tier }));

  useEffect(() => {
    const currentIds = new Set(plans.map((p) => p.planId));
    setSelectedTiers((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => currentIds.has(id))));
  }, [comparablePlans]);

  const drawerTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.modalTitle',
  );

  const accessibilityContent = retrieveContentfulData<Record<string, string>>(
    'health_section.secondary_content.accessibility',
  );

  const a11yAddPlanAlert = accessibilityContent?.compare_modal_remove_plan_alert.replace(
    /{x}/,
    selectedPlan.planMarketingName,
  );

  const a11yRemovePlanAlert = accessibilityContent?.compare_modal_remove_plan_alert.replace(
    /{x}/,
    selectedPlan.planMarketingName,
  );

  const plans = comparablePlans.length > 0 ? comparablePlans : reviewModePlans;
  const HeaderComponent = showCompareRows ? FloatingHeaderRow : FloatingHeaderRowTableView;

  // Choose button component
  const renderChooseButtons = () => {
    return (
      <ChooseButtons
        selectedPlan={selectedPlan}
        reviewModePlans={reviewModePlans}
        choosePlan={(plan) => {
          /* istanbul ignore next */
          setHiddenAlert(a11yAddPlanAlert);
          choosePlan(plan);
        }}
        deselectPlan={() => {
          /* istanbul ignore next */
          setHiddenAlert(a11yRemovePlanAlert);
          deselectPlan();
        }}
        hideAsterikIfCompareDrawer={!showCompareRows}
      />
    );
  };

  const renderTableContainer = () => (
    <CompareContainer
      id="intersection-root"
      data-testid={showCompareRows ? 'compare-view-container' : 'review-mode-table-view-container'}
    >
      <div ref={intersectionRef} id="compare-modal-intersection-indicator" />

      <HeaderComponent shadow={!inView}>
        <PlanHeader displaySmallTitle={!inView} plans={plans} showCompareRows={showCompareRows} />
      </HeaderComponent>
      {!showCompareRows && renderChooseButtons()}

      <MainDetails
        plans={plans}
        hsaContributionLimit={hsaContributionLimit}
        maximumAvailableAnnualSihraContribution={maximumAvailableAnnualSihraContribution}
        killMedRec={killMedRec}
        hsaBalance={hsaBalance}
        showOonInPbw={showOonInPbw}
        isNetworkTierToggleEnabled={is_network_tier_toggle_enabled ?? false}
        getSelectedTier={getSelectedTier}
        setTierForPlan={setTierForPlan}
      />

      <DeductiblesLimits plans={plans} showCompareRows={showCompareRows} getSelectedTier={getSelectedTier} />

      {/* Keeping this section for Primary Care, Specialist Care and NON_FORMULARY_DRUG fields */}
      <Copays plans={plans} getSelectedTier={getSelectedTier} />

      <RxCoverage plans={plans} getSelectedTier={getSelectedTier} />
      {showCompareRows && renderChooseButtons()}

      {!showCompareRows && (
        <RowContainer role="row">
          <Cell className="row-header" hideRowHeader={false} role="rowheader" gray={false} size="large">
            <div className="asterisk">
              <Paragraph size="mini">
                * <Text field="health_section.compare_plan_text.compareModal.afterDeductibleAsterisk" />
              </Paragraph>
            </div>
          </Cell>
          <Cell role="cell" size="small" />
          <Cell role="cell" size="small" />
          <Cell role="cell" size="small" />
        </RowContainer>
      )}
    </CompareContainer>
  );

  return showCompareRows ? (
    <Drawer isOpen={isOpen} onClose={handleClose} title={drawerTitle} size="xl">
      {renderTableContainer()}
      <AriaHidden aria-live="polite">{hiddenAlert}</AriaHidden>
    </Drawer>
  ) : (
    renderTableContainer()
  );
};

export default CompareModal;
